import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { EventDateSelectorV2 } from 'src/core/POS/DateRangeSelector/EventDateSelectorV2';
import { PosMultiSelect } from 'src/core/POS/PosMultiSelect';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { OnSaleEventTableColumnId } from 'src/navigations/Routes/OnSaleDiscovery/OnSaleDiscoveryView.types';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID,
  PARENT_CATEGORY_TO_CID,
  TOP_LEVEL_CATEGORY_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  DateRangePresetName,
  NextDateRangeRelativePresetNames,
} from 'src/utils/dateTimeUtils';
import {
  NumericRange,
  OnSaleEventQuery,
  TopLevelCategory,
} from 'src/WebApiController';

import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
  useLocationFilter,
} from '../Filters';
import { NumberRangeFilter } from '../Filters/NumberRangeFilter';

type MetricsFilterEntry = {
  contentId: OnSaleEventTableColumnId;
  filterKey: keyof OnSaleEventQuery;
};

const MetricsFilters: MetricsFilterEntry[] = [
  {
    contentId: OnSaleEventTableColumnId.ListingCount,
    filterKey: 'listingQtyRange',
  },
  {
    contentId: OnSaleEventTableColumnId.TicketCount,
    filterKey: 'ticketQtyRange',
  },
  {
    contentId: OnSaleEventTableColumnId.UnitNetProceedsMin,
    filterKey: 'priceMinRange',
  },
  {
    contentId: OnSaleEventTableColumnId.UnitNetProceedsP25,
    filterKey: 'priceP25Range',
  },
  {
    contentId: OnSaleEventTableColumnId.UnitNetProceedsP50,
    filterKey: 'priceP50Range',
  },
  {
    contentId: OnSaleEventTableColumnId.VisitsLastHour,
    filterKey: 'visitsL1HRange',
  },
  {
    contentId: OnSaleEventTableColumnId.VisitsLastDay,
    filterKey: 'visitsL24HRange',
  },
  {
    contentId: OnSaleEventTableColumnId.VisitsLast3Days,
    filterKey: 'visitsL3DRange',
  },
  {
    contentId: OnSaleEventTableColumnId.VisitsLast7Days,
    filterKey: 'visitsL7DRange',
  },
  {
    contentId: OnSaleEventTableColumnId.TransactionsLastHour,
    filterKey: 'transL1HRange',
  },
  {
    contentId: OnSaleEventTableColumnId.TransactionsLastDay,
    filterKey: 'transL24HRange',
  },
  {
    contentId: OnSaleEventTableColumnId.TransactionsLast3Days,
    filterKey: 'transL3DRange',
  },
  {
    contentId: OnSaleEventTableColumnId.TransactionsLast7Days,
    filterKey: 'transL7DRange',
  },
];
const NextDateRangePresetNames = [
  DateRangePresetName.Today,
  DateRangePresetName.NextWeek,
  DateRangePresetName.NextMonth,
];

export function useDiscoveryFilters() {
  const { tempQuery, setTempQuery } = useFilterQueryContext<OnSaleEventQuery>();

  const locationFilters = useLocationFilter({});

  const eventFilter = useMemo((): FilterToolbarGroup => {
    const defaultFilter: FilterToolbarItem[] = [
      {
        labelContentId: ContentId.OnSaleDate,
        filterId: 'onSaleDates' as FilterToolbarItemId,
        filterQueryKeys: ['onSaleDates'],
        filterItem: (
          <EventDateSelectorV2
            value={tempQuery.onSaleDates}
            includePast={false}
            exactPresetNames={NextDateRangePresetNames}
            trailingPresetNames={NextDateRangeRelativePresetNames}
            onChange={({ value: dateRange }) => {
              if (!isEqual(dateRange, tempQuery.onSaleDates)) {
                setTempQuery({
                  ...tempQuery,
                  onSaleDates: dateRange ?? null,
                });
              }
            }}
          />
        ),
      },
    ];

    defaultFilter.push(...(locationFilters as FilterToolbarItem[]));

    // genre filters
    defaultFilter.push({
      filterId: 'topLevelCategories' as FilterToolbarItemId,
      filterQueryKeys: ['topLevelCategories'] as FilterToolbarItemId[],
      labelContentId: ContentId.Genre,
      filterItem: (
        <PosEnumMultiSelect
          triggerProps={{ style: { width: '100%' } }}
          values={
            // TODO: set up enum type for this prop
            (tempQuery.topLevelCategories as TopLevelCategory[]) || []
          }
          placeholderText={ContentId.AllGenres}
          enableEmptySelection
          onChange={({ newValues: topLevelCategories }) => {
            const isChanged = !isEqual(
              topLevelCategories,
              tempQuery.topLevelCategories
            );

            setTempQuery({
              ...tempQuery,
              topLevelCategories: isChanged ? topLevelCategories : [],
            });
          }}
          valueOptionsContent={TOP_LEVEL_CATEGORY_TO_CID}
        />
      ),
    });
    defaultFilter.push({
      filterId: 'parentCategories' as FilterToolbarItemId,
      labelContentId: ContentId.SubGenre,
      filterQueryKeys: ['parentCategories'] as FilterToolbarItemId[],
      filterItem: (
        <PosMultiSelect
          triggerProps={{ style: { width: '100%' } }}
          values={
            tempQuery.parentCategories
              ? tempQuery.parentCategories.map((c) => String(c))
              : []
          }
          onChange={({ newValues }) => {
            setTempQuery({
              ...tempQuery,
              parentCategories:
                newValues.length > 0
                  ? newValues.map((value) => Number(value))
                  : null,
            });
          }}
          searchable={true}
          valueOptionsContent={PARENT_CATEGORY_TO_CID}
        />
      ),
    });

    return {
      titleContentId: ContentId.Events,
      type: 'row',
      items: defaultFilter,
    };
  }, [tempQuery, locationFilters, setTempQuery]);

  const metricsFilter = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Metrics,
      type: 'row',
      items: MetricsFilters.map(({ contentId, filterKey }) => ({
        filterId: filterKey,
        labelContentId: ON_SALE_EVENT_TABLE_COLUMN_ID_CONTENT_ID[
          contentId
        ] as ContentId,
        filterQueryKeys: [filterKey],
        filterItem: (
          <NumberRangeFilter
            hideActions
            filterValue={{
              min: (tempQuery[filterKey] as NumericRange)?.start ?? undefined,
              max: (tempQuery[filterKey] as NumericRange)?.end ?? undefined,
            }}
            setFilterValue={(value) => {
              if (value == null) {
                setTempQuery({
                  ...tempQuery,
                  [filterKey]: null,
                });
              }

              setTempQuery({
                ...tempQuery,
                [filterKey]: {
                  start: value?.min ?? null,
                  end: value?.max ?? null,
                },
              });
            }}
          />
        ),
      })),
    }),
    [setTempQuery, tempQuery]
  );

  const filters = useMemo((): FilterToolbarGroup[] => {
    return [eventFilter, metricsFilter];
  }, [eventFilter, metricsFilter]);

  return { filters };
}
